// 2026-10-26 · Recuperación / comprobación del identificador del dispositivo.
//
// Caso real (Mediterránea Golf, prod): un TPV (PC Windows) con JWT válido pero
// SIN `SessionIdentifier` (su fila `Devices` estaba cerrada desde el 08-10 y la
// pestaña seguía operando) → todo respondía 200 salvo `POST mesas/{id}/lease`,
// que exige la cabecera → 400 DEVICE_REQUIRED en cada entrada a una mesa y en
// cada vuelta del tiempo real. Lo mismo pasaba tras el registro público por OTP
// (el API devolvía "" y `setSession` lo guardaba tal cual).
//
// `ensure()` (antes de cada lease; barato):
//   - Hay identificador (cabecera o guardado) → se asegura en ambos y se devuelve.
//   - No hay → `POST authentication/EnsureDevice`: el API da de alta el
//     dispositivo (como el login) y se guarda.
// `verify()` (al arrancar la app con sesión): pregunta SIEMPRE al API. Si el
//   identificador ya no es un dispositivo activo (cerrado desde otro
//   dispositivo, límite de sesiones) → `sessionClosed`: se avisa con
//   DEVICE_SESSION_CLOSED_EVENT para volver al login con un mensaje claro, en
//   lugar de seguir operando «medio logueado».
// En ambos:
//   - El API responde sin identificador (administrador, sesión cerrada) o no
//     tiene el endpoint → no se vuelve a pedir en esta carga: el TPV sigue sin
//     lease (degradación silenciosa) en lugar de lanzar llamadas condenadas.
//   - Error de red → se reintenta como pronto tras RETRY_COOLDOWN_MS.
// Una sola petición en vuelo (las llamadas concurrentes esperan la misma).

import axios from "src/utils/axios";
import {
  applySessionIdentifier,
  currentSessionIdentifier,
  normalizeSessionIdentifier,
} from "./deviceSessionCore";

export const ENSURE_DEVICE_URL = "authentication/EnsureDevice";
export const RETRY_COOLDOWN_MS = 60_000;
/** Evento de `window` cuando el API confirma que la sesión de este dispositivo está cerrada. */
export const DEVICE_SESSION_CLOSED_EVENT = "tmo:device-session-closed";

function defaultNotifyClosed(detail) {
  try {
    if (typeof window !== "undefined" && typeof window.CustomEvent === "function") {
      window.dispatchEvent(new window.CustomEvent(DEVICE_SESSION_CLOSED_EVENT, { detail }));
    }
  } catch {
    /* ignore */
  }
}

export function createDeviceSessionRecovery({
  http,
  storage,
  getHeaders = () => http?.defaults?.headers?.common,
  now = () => Date.now(),
  notifyClosed = defaultNotifyClosed,
} = {}) {
  let inflight = null;
  let blocked = false; // el servidor dijo que no hay dispositivo para esta sesión
  let lastFailureAt = null;

  const current = () => currentSessionIdentifier({ storage, headers: getHeaders() });

  async function ask() {
    const sent = current();
    try {
      const res = await http.post(ENSURE_DEVICE_URL);
      const data = res?.data || {};
      const sid = normalizeSessionIdentifier(data.sessionIdentifier ?? data.userSessionIdentifier);
      if (!sid) {
        blocked = true;
        if (data.sessionClosed && sent) {
          // Sesión cerrada en el servidor: no se sigue mandando un dispositivo muerto.
          applySessionIdentifier(null, { storage, headers: getHeaders() });
          notifyClosed({ sessionIdentifier: sent });
        }
        return null;
      }
      applySessionIdentifier(sid, { storage, headers: getHeaders() });
      return sid;
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      // API sin el endpoint (o sin permiso): no insistir en esta carga.
      if (status === 401 || status === 403 || status === 404 || status === 405) blocked = true;
      else lastFailureAt = now();
      // Con identificador guardado se sigue usando (sin red no se puede comprobar).
      return sent;
    }
  }

  function run() {
    if (!inflight) {
      inflight = ask().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  }

  const coolingDown = () => lastFailureAt != null && now() - lastFailureAt < RETRY_COOLDOWN_MS;

  return {
    /** Identificador efectivo (sin pedir nada al servidor). */
    current,
    async ensure() {
      const sid = current();
      if (sid) {
        // La cabecera pudo perderse (o venir "") con el guardado intacto: se re-aplica.
        applySessionIdentifier(sid, { storage, headers: getHeaders() });
        return sid;
      }
      if (blocked || coolingDown()) return null;
      return run();
    },
    /** Comprueba con el API que el dispositivo sigue activo (o lo da de alta). */
    async verify() {
      if (blocked) return current();
      if (coolingDown()) return current();
      return run();
    },
    isBlocked: () => blocked,
    /** Nueva sesión (login/logout): se puede volver a intentar. */
    reset() {
      blocked = false;
      lastFailureAt = null;
    },
  };
}

const defaultRecovery = createDeviceSessionRecovery({ http: axios });

export const ensureDeviceSessionIdentifier = () => defaultRecovery.ensure();
export const verifyDeviceSession = () => defaultRecovery.verify();
export const currentDeviceSessionIdentifier = () => defaultRecovery.current();
export const resetDeviceSessionRecovery = () => defaultRecovery.reset();
export const isDeviceSessionRecoveryBlocked = () => defaultRecovery.isBlocked();
